import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { isDefined } from 'common/utils';

import { DraftStatus, SelectionState, SharedMenu } from './types';

export const getVenuesLinkedToSharedMenu = (linkedVenueIds: string[] | null, venues: Venue[]) => {
  if (!linkedVenueIds) {
    return [];
  }

  return linkedVenueIds.map(id => venues.find(venue => venue.id === id)).filter(isDefined);
};

export const isSharedMenuBootstrapping = (draft: DraftStatus) =>
  draft?.bootstrapping?.status === 'pending' || draft?.bootstrapping?.status === 'running';

export const isSharedMenuBootstrappingFailure = (draft: DraftStatus) => draft?.bootstrapping?.status === 'failure';

export const isNewSelection = (selection?: SharedMenu) => selection?.version.number === 1;

/**
 * Returns a typed array of keys from an object since `Object.keys` doesn't.
 *
 * `const myCoolObj: Record<MyCoolType, any> = { ... }`
 *
 * `const keys = getTypedKeys(myCoolObj)`
 *
 * => `keys` will be of type `MyCoolType[]`
 *
 * `const keys = Object.keys(myCoolObj)`
 *
 * => `keys` will be of type `string[]`
 */
export const getTypedKeys = <T extends Record<string, unknown>>(object: T): (keyof T)[] => Object.keys(object);

export const getSelectionState = (selection: SharedMenu): SelectionState => {
  if (isSharedMenuBootstrapping(selection.draft)) {
    return 'bootstrapping';
  }

  if (isNewSelection(selection)) {
    return 'draft';
  }

  if (!!selection.draft && !selection.draft.publish_at) {
    return 'pending-changes';
  }

  if (!!selection.draft?.publish_at) {
    return 'scheduled-changes';
  }

  return 'live';
};

export const checkIfSelectionCanBePublished = (selection: SharedMenu) => {
  const bootstrappingFailed = selection.draft?.bootstrapping?.status === 'failure';
  const bootstrappingInProgress = isSharedMenuBootstrapping(selection.draft);
  return !!selection.draft && !bootstrappingFailed && !bootstrappingInProgress;
};

export const selectionHasValidLastPublishedValue = (selection: SharedMenu) =>
  !isNewSelection(selection) && selection.version.updated_at;
